import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FemaleCalculatorSection } from "@/components/FemaleCalculatorSection";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { idealWomanFaq } from "@/data/landingFaqs";

const PATH = "/ideal-woman-calculator";

const IdealWomanCalculator = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Ideal Woman Calculator",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: meta?.structuredData,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      {/* Hero Section */}
      <header className="border-b bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Ideal Woman Calculator
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Describe your ideal woman — age, height, body type, income, education,
              and more — and see what percentage of US women actually match. Every
              filter is backed by US Census Bureau, CDC, and Pew Research data, so the
              number you get is real, not a guess.
            </p>
            <p className="text-sm text-muted-foreground">
              Looking for the male version?{" "}
              <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                Try the Male Partner Calculator
              </Link>
            </p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-12">
        <FemaleCalculatorSection sharePath={PATH} />

        {/* What it measures */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">What the ideal woman calculator measures</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Each criterion you select maps to a measured share of the US female
              population: age brackets come from Census Bureau population estimates,
              height and body type from CDC NHANES surveys, and income, education, and
              marital status from the American Community Survey and Pew Research. The
              calculator multiplies those shares together to estimate how many women
              meet all of your standards at once.
            </p>
            <p>
              That multiplication is the part most people underestimate. A preference
              that matches 30% of women and another that matches 20% do not average
              out to 25% — combined, they match just 6%. Add a third or fourth filter
              and your "ideal girl" can quickly become rarer than one in a hundred.
              The{" "}
              <Link to="/methodology" className="text-primary hover:underline">
                methodology page
              </Link>{" "}
              documents the full model, its assumptions, and its limitations.
            </p>
          </div>
        </section>

        {/* Realistic expectations */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">A reality check, not a verdict</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              A low percentage does not mean your ideal woman does not exist. There
              are roughly 57 million women aged 18-44 in the United States, so even a
              1-in-200 result still describes hundreds of thousands of real people.
              What the number does show is which of your standards costs you the most
              of the dating pool — and that is useful information when deciding which
              preferences are genuine requirements and which are nice-to-haves.
            </p>
            <p>
              Want to dig into the underlying numbers? Browse our{" "}
              <Link to="/stats" className="text-primary hover:underline">
                US dating statistics hub
              </Link>{" "}
              for research on income, education, height, and body type distributions,
              or check the{" "}
              <Link to="/states" className="text-primary hover:underline">
                dating pool in your state
              </Link>
              . You can also test how demanding your criteria are overall with the{" "}
              <Link to="/dating-standards-calculator" className="text-primary hover:underline">
                dating standards calculator
              </Link>
              .
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          <div className="space-y-6">
            {idealWomanFaq.map((item) => (
              <div key={item.question} className="border rounded-lg p-6">
                <h3 className="font-semibold mb-2">{item.question}</h3>
                <p className="text-sm text-muted-foreground">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="max-w-4xl mx-auto">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-r-lg">
            <h3 className="font-semibold mb-4">Statistical Disclaimer</h3>
            <div className="text-sm space-y-2">
              <p>
                This calculator provides statistical probabilities based on
                demographic data and should not be interpreted as definitive
                predictions about individual relationships or dating outcomes.
              </p>
              <p>
                Results reflect population-level statistics and do not account for
                geography, social circles, or individual compatibility beyond the
                measured criteria.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IdealWomanCalculator;
